import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import ServiceWorkerHelper from '../utils/serviceWorkerHelper'

// 添加动画样式
const styles = `
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translate(-50%, -20px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
  
  .animate-fade-in-down {
    animation: fadeInDown 0.3s ease-out;
  }
`

export const CORSErrorHandler = () => {
  const [showError, setShowError] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const location = useLocation()

  const excludedRoutes = ['/my-games', '/my-world', '/world-simulator/profile']
  const isExcludedRoute = excludedRoutes.some((path) =>
    location.pathname.startsWith(path)
  )

  useEffect(() => {
    if (isExcludedRoute && showError) {
      setShowError(false)
    }
  }, [isExcludedRoute, showError])

  useEffect(() => {
    if (isExcludedRoute) {
      return
    }

    // 只在首次加载时检查一次，避免重复提示
    let hasShown = false
    let errorCount = 0
    const maxErrors = 3 // 最多显示3次错误后就不再显示
    
    // 检查是否已经处理过（使用 sessionStorage）
    const hasHandled = sessionStorage.getItem('cors-error-handled')
    if (hasHandled === 'true') {
      return // 本次会话已经处理过，不再显示
    }
    
    // 监听全局错误
    const handleError = (event) => {
      if (hasShown || errorCount >= maxErrors) return
      
      const errorMessage = event.message || event.error?.message || ''
      const errorTarget = event.target || event.srcElement
      
      // 检测 CORS 错误
      const isCorsError = 
        errorMessage.includes('CORS') || 
        errorMessage.includes('Access-Control-Allow-Origin') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('NetworkError') ||
        (errorTarget && errorTarget.tagName === 'IMG' && errorTarget.src && (
          errorTarget.src.includes('ark-content-generation') ||
          errorTarget.src.includes('volces.com') ||
          errorTarget.src.includes('volcengine.com')
        ))
      
      if (isCorsError) {
        errorCount++
        console.warn('检测到 CORS 错误，可能需要更新 Service Worker', errorCount)
        if (!hasShown) {
          setShowError(true)
          hasShown = true
        }
      }
    }

    // 监听资源加载错误
    const handleResourceError = (event) => {
      if (hasShown || errorCount >= maxErrors) return
      
      const target = event.target
      if (target && target.tagName === 'IMG') {
        // 检查图片是否已经被组件内部处理（src 为空或 display 为 none）
        // 如果已经被处理，就静默忽略，不显示错误提示
        if (!target.src || target.style.display === 'none') {
          return
        }
        
        const src = target.src || ''
        // 检查是否是 403 错误（权限问题，通常是 URL 过期）
        const is403Error = event.type === 'error' && (
          src.includes('ark-content-generation') ||
          src.includes('volces.com') ||
          src.includes('volcengine.com') ||
          src.includes('doubao.com')
        )
        
        if (is403Error) {
          // 对于 403 错误，静默处理，不显示错误提示
          // 这些通常是 URL 过期导致的，组件会显示占位符
          return
        }
        
        // 其他类型的错误才显示提示
        if (
          src.includes('ark-content-generation') ||
          src.includes('volces.com') ||
          src.includes('volcengine.com') ||
          src.includes('doubao.com')
        ) {
          errorCount++
          console.warn('检测到外部图片加载失败，可能需要更新 Service Worker', errorCount)
          if (!hasShown) {
            setShowError(true)
            hasShown = true
          }
        }
      }
    }

    // 监听未处理的 Promise 拒绝
    const handleUnhandledRejection = (event) => {
      if (hasShown || errorCount >= maxErrors) return
      
      const reason = event.reason
      const errorMessage = reason?.message || String(reason) || ''
      
      if (
        errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control-Allow-Origin') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('NetworkError')
      ) {
        errorCount++
        console.warn('检测到未处理的 CORS 错误', errorCount)
        if (!hasShown) {
          setShowError(true)
          hasShown = true
        }
      }
    }

    // 延迟启动监听，避免首次加载时的误报
    const timer = setTimeout(() => {
      window.addEventListener('error', handleError, true)
      window.addEventListener('error', handleResourceError, true)
      window.addEventListener('unhandledrejection', handleUnhandledRejection)
    }, 2000) // 2秒后再开始监听

    return () => {
      clearTimeout(timer)
      window.removeEventListener('error', handleError, true)
      window.removeEventListener('error', handleResourceError, true)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [isExcludedRoute])

  const handleUpdate = async () => {
    setIsUpdating(true)
    
    try {
      // 标记为已处理，避免重复提示
      sessionStorage.setItem('cors-error-handled', 'true')
      
      const success = await ServiceWorkerHelper.forceUpdate()
      
      if (success) {
        // 等待一秒后刷新页面
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        setIsUpdating(false)
        // 清除标记，允许重试
        sessionStorage.removeItem('cors-error-handled')
        alert('自动更新失败，请尝试手动刷新页面（Ctrl+Shift+R）')
      }
    } catch (error) {
      console.error('更新失败:', error)
      setIsUpdating(false)
      // 清除标记，允许重试
      sessionStorage.removeItem('cors-error-handled')
      alert('更新失败，请尝试手动刷新页面（Ctrl+Shift+R）')
    }
  }
  
  const handleDismiss = () => {
    // 标记为已处理，本次会话不再显示
    sessionStorage.setItem('cors-error-handled', 'true')
    setShowError(false)
  }

  if (isExcludedRoute || !showError) return null

  return (
    <>
      <style>{styles}</style>
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] max-w-2xl w-full px-4">
        <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg shadow-2xl p-5 border border-white/20 backdrop-blur-sm animate-fade-in-down">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg mb-2">图片加载失败</h3>
              <p className="text-white/90 text-sm mb-4 leading-relaxed">
                检测到跨域资源加载错误（CORS）。这通常是因为 Service Worker 需要更新。
                <br />
                点击下方按钮自动修复此问题。
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white text-red-500 rounded-lg font-medium hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  <RefreshCw size={18} className={isUpdating ? 'animate-spin' : ''} />
                  {isUpdating ? '更新中...' : '立即修复'}
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-6 py-2.5 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors"
                >
                  稍后
                </button>
              </div>
              <p className="text-white/70 text-xs mt-3">
                💡 提示：如果自动修复失败，请按 <kbd className="px-2 py-1 bg-white/20 rounded">Ctrl+Shift+R</kbd> 强制刷新页面
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

